// components/MemberPortal/Dashboard.js - Complete revised version with task integration + fixed rating modal overlay on mobile
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../axiosConfig';
import ChatWindow from './ChatWindow';
import ScheduleRoundModal from './ScheduleRoundModal';

const MemberPortalDashboard = ({ memberProfile }) => {
  const [activeTab, setActiveTab] = useState('browse');
  const [nearbyClubs, setNearbyClubs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [activeRequestClubIds, setActiveRequestClubIds] = useState(new Set());
  const [hostingEnabled, setHostingEnabled] = useState(
    Boolean(memberProfile?.is_hosting_enabled ?? true)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Chat state
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // NEW: Rating notification state
  const [needsRating, setNeedsRating] = useState([]);
  const [showRatingBadge, setShowRatingBadge] = useState(false);
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/auth/current-user/');
        setCurrentUserId(res?.data?.id || res?.data?.user?.id || null);
      } catch (e) {
        setCurrentUserId(null);
      }
    })();
  }, []);

  useEffect(() => {
    setHostingEnabled(Boolean(memberProfile?.is_hosting_enabled ?? true));
  }, [memberProfile?.is_hosting_enabled]);

  useEffect(() => {
    loadTabData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // If hosting gets turned off while on Browse, move user away
  useEffect(() => {
    if (!hostingEnabled && activeTab === 'browse') {
      setActiveTab('member-card');
    }
  }, [hostingEnabled, activeTab]);

  // NEW: Check for experiences needing rating on component mount and periodically
  useEffect(() => {
    const checkForRatings = async () => {
      try {
        const response = await axiosInstance.get('/member-portal/check-experiences-for-rating/');
        const { needs_rating = [], count = 0 } = response.data || {};
        setNeedsRating(needs_rating);
        setShowRatingBadge(count > 0);

        // Show rating prompt if there are unrated experiences and user hasn't seen it recently
        if (count > 0 && !localStorage.getItem('rating-prompt-shown')) {
          setShowRatingPrompt(true);
          localStorage.setItem('rating-prompt-shown', Date.now().toString());
        }
      } catch (error) {
        console.error('Error checking for rating experiences:', error);
      }
    };

    checkForRatings();

    // Check every 5 minutes for new experiences to rate
    const interval = setInterval(checkForRatings, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // NEW: Clear rating prompt after 24 hours
  useEffect(() => {
    const lastShown = localStorage.getItem('rating-prompt-shown');
    if (lastShown) {
      const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
      if (parseInt(lastShown) < dayAgo) {
        localStorage.removeItem('rating-prompt-shown');
      }
    }
  }, []);

  const loadTabData = async (tab) => {
    setLoading(true);
    setError(null);
    try {
      switch (tab) {
        case 'browse': {
          // Don't load browse data if hosting disabled
          if (!hostingEnabled) {
            setNearbyClubs([]);
            setActiveRequestClubIds(new Set());
            break;
          }
          // Fetch clubs and user's active requests in parallel
          const [clubsResponse, requestsResponse] = await Promise.all([
            axiosInstance.get('/member-portal/clubs/with_hosts/?has_hosts=true'),
            axiosInstance.get('/member-portal/play-requests/')
          ]);
          const raw = Array.isArray(clubsResponse.data) ? clubsResponse.data : [];
          const filtered = raw.filter(c => (c.host_count ?? 0) > 0);
          setNearbyClubs(filtered);

          // Build set of club IDs with active requests
          const activeStatuses = ['pending', 'scheduled', 'confirmed'];
          const allRequests = requestsResponse.data || [];
          const activeClubIds = new Set(
            allRequests
              .filter(req => activeStatuses.includes(req.status))
              .map(req => req.target_club?.club_id || req.target_club)
          );
          setActiveRequestClubIds(activeClubIds);
          break;
        }
        case 'requests': {
          const requestsResponse = await axiosInstance.get('/member-portal/play-requests/');
          setRequests(requestsResponse.data || []);
          break;
        }
        case 'experiences': {
          const experiencesResponse = await axiosInstance.get('/member-portal/play-experiences/');
          setExperiences(experiencesResponse.data || []);
          // Refresh rating check when viewing experiences
          try {
            const ratingResponse = await axiosInstance.get('/member-portal/check-experiences-for-rating/');
            const { needs_rating = [], count = 0 } = ratingResponse.data || {};
            setNeedsRating(needs_rating);
            setShowRatingBadge(count > 0);
          } catch (e) {
            console.error('Error refreshing rating check:', e);
          }
          break;
        }
        case 'member-card':
        default:
          break;
      }
    } catch (err) {
      console.error(`Error loading ${tab} data:`, err);
      setError(`Failed to load ${tab} data`);
    } finally {
      setLoading(false);
    }
  };

  const sendPlayRequest = async (club) => {
    const clubId = club.club_id || club.id;

    // Double-check frontend state (in case of stale data)
    if (activeRequestClubIds.has(clubId)) {
      alert('You already have an active request at this club. Check your Requests tab for details.');
      return;
    }

    try {
      const payload = {
        target_club: clubId,
        message: `Are you available for a guest round?`,
      };
      await axiosInstance.post('/member-portal/play-requests/', payload);
      alert('Request sent successfully! You and your host will receive email notifications.');

      // Update active request tracking immediately
      setActiveRequestClubIds(prev => new Set([...prev, clubId]));

      if (activeTab === 'requests') loadTabData('requests');
    } catch (err) {
      console.error('Error sending request:', err);
      // Handle duplicate request error from backend
      if (err.response?.status === 400 && err.response?.data?.existing_request_id) {
        alert('You already have an active request at this club. Check your Requests tab for details.');
        // Refresh the active requests to sync state
        loadTabData('browse');
      } else {
        alert('Error sending request. Please try again.');
      }
    }
  };

  const handleToggleHosting = useCallback(async (enabled) => {
    try {
      const res = await axiosInstance.post('/member-portal/profiles/hosting-toggle/', {
        enabled
      });
      setHostingEnabled(Boolean(res?.data?.is_hosting_enabled ?? enabled));
    } catch (e) {
      alert('Unable to update hosting status. Please try again.');
    }
  }, []);

  const handleViewMessage = (request) => {
    setSelectedRequest(request);
    setShowChat(true);
  };

  const handleScheduleRound = () => {
    setShowScheduleModal(true);
  };

  const handleDenyRequest = async (requestId, message = '') => {
    try {
      await axiosInstance.post(`/member-portal/play-requests/${requestId}/deny/`, {
        message
      });
      loadTabData('requests');
      setShowChat(false);
    } catch (error) {
      console.error('Error denying request:', error);
      alert('Error denying request. Please try again.');
    }
  };

  // NEW: Handle rating prompt actions
  const handleGoToExperiences = () => {
    setShowRatingPrompt(false);
    setActiveTab('experiences');
  };

  const handleDismissRatingPrompt = () => {
    setShowRatingPrompt(false);
    // Don't show again for 24 hours
    localStorage.setItem('rating-prompt-shown', Date.now().toString());
  };

  const tabs = [
    { key: 'browse', label: 'Browse Clubs', icon: '🔍' },
    { key: 'requests', label: 'Requests', icon: '📝' },
    {
      key: 'experiences',
      label: 'Experiences',
      icon: '⛳',
      badge: showRatingBadge ? needsRating.length : null
    },
    { key: 'member-card', label: 'Member Card', icon: '🏌️' },
  ];

  return (
    <div className="member-portal-dashboard">
      <div className="portal-header">
        <h2>Member Portal</h2>
        <div className="welcome-message">
          Welcome, {memberProfile?.user?.first_name || memberProfile?.user?.username || 'Member'}!
          {memberProfile?.home_club?.name && (
            <span className="home-club">Member at {memberProfile.home_club.name}</span>
          )}
        </div>
        <div className="verification-badge">✅ Verified Member</div>
      </div>

      {/* NEW: Rating prompt notification */}
      {showRatingPrompt && (
        <div className="rating-prompt-banner">
          <div className="rating-prompt-content">
            <span className="rating-prompt-icon">⭐</span>
            <div className="rating-prompt-text">
              <h4>Rate Your Recent Golf Experiences</h4>
              <p>You have {needsRating.length} round{needsRating.length !== 1 ? 's' : ''} to rate. Help build our community by sharing your feedback!</p>
            </div>
            <div className="rating-prompt-actions">
              <button className="rate-now-button" onClick={handleGoToExperiences}>
                Rate Now
              </button>
              <button className="dismiss-button" onClick={handleDismissRatingPrompt}>
                Later
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="portal-tabs">
        {tabs.map(tab => {
          // disable Browse tab if hosting disabled
          const isBrowse = tab.key === 'browse';
          const disabled = isBrowse && !hostingEnabled;
          return (
            <button
              key={tab.key}
              className={`tab ${activeTab === tab.key ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => {
                if (disabled) {
                  alert('Enable Hosting to browse clubs and send requests.');
                  return;
                }
                setActiveTab(tab.key);
              }}
              disabled={disabled}
              aria-disabled={disabled}
              title={disabled ? 'Enable Hosting to browse clubs' : undefined}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
              {tab.badge && (
                <span className="notification-badge">{tab.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="portal-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner2"></div>
            <p>Loading...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={() => loadTabData(activeTab)}>Retry</button>
          </div>
        ) : (
          <div className="tab-content">
            {activeTab === 'browse' && (
              hostingEnabled ? (
                <BrowseClubsTab
                  clubs={nearbyClubs}
                  onSendRequest={sendPlayRequest}
                  userHomeClub={memberProfile?.home_club}
                  activeRequestClubIds={activeRequestClubIds}
                />
              ) : (
                <BrowseGate onGoToMemberCard={() => setActiveTab('member-card')} />
              )
            )}
            {activeTab === 'requests' && (
              <RequestsTab
                requests={requests}
                onUpdate={() => loadTabData('requests')}
                currentUserId={currentUserId}
                onViewMessage={handleViewMessage}
                onDenyRequest={handleDenyRequest}
              />
            )}
            {activeTab === 'experiences' && (
              <ExperiencesTab
                experiences={experiences}
                needsRating={needsRating}
                onUpdate={() => loadTabData('experiences')}
                currentUserId={currentUserId}
              />
            )}
            {activeTab === 'member-card' && (
              <MemberCardTab
                memberProfile={memberProfile}
                hostingEnabled={hostingEnabled}
                onToggleHosting={handleToggleHosting}
              />
            )}
          </div>
        )}
      </div>

      {/* Chat Window Modal */}
      {showChat && selectedRequest && (
        <ChatWindow
          playRequest={selectedRequest}
          currentUserId={currentUserId}
          onClose={() => setShowChat(false)}
          onScheduleRound={handleScheduleRound}
          onDenyRequest={(message) => handleDenyRequest(selectedRequest.request_id, message)}
          onUpdate={() => loadTabData('requests')}
        />
      )}

      {/* Schedule Round Modal */}
      {showScheduleModal && selectedRequest && (
        <ScheduleRoundModal
          playRequest={selectedRequest}
          onClose={() => setShowScheduleModal(false)}
          onScheduled={() => {
            setShowScheduleModal(false);
            loadTabData('requests');
          }}
        />
      )}

      {/* NEW: Add CSS styles for rating prompt */}
      <style jsx>{`
        .rating-prompt-banner {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: 2px solid #28a745;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
          box-shadow: 0 2px 8px rgba(40, 167, 69, 0.1);
        }

        .rating-prompt-content {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .rating-prompt-icon {
          font-size: 2rem;
          flex-shrink: 0;
        }

        .rating-prompt-text {
          flex: 1;
        }

        .rating-prompt-text h4 {
          margin: 0 0 4px 0;
          color: #28a745;
          font-weight: 600;
        }

        .rating-prompt-text p {
          margin: 0;
          color: #6c757d;
          font-size: 0.95rem;
        }

        .rating-prompt-actions {
          display: flex;
          gap: 12px;
          flex-shrink: 0;
        }

        .rate-now-button {
          background: #28a745;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .rate-now-button:hover {
          background: #218838;
          transform: translateY(-1px);
        }

        .dismiss-button {
          background: transparent;
          color: #6c757d;
          border: 1px solid #dee2e6;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .dismiss-button:hover {
          background: #f8f9fa;
          color: #495057;
        }

        .notification-badge {
          background: #dc3545;
          color: white;
          border-radius: 10px;
          padding: 2px 6px;
          font-size: 12px;
          font-weight: bold;
          margin-left: 8px;
          min-width: 18px;
          text-align: center;
          animation: pulse 2s infinite;
        }

        .tab.active .notification-badge {
          background: #fff;
          color: #007bff;
          animation: none;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        .loading-spinner {
          border: 3px solid #f3f3f3;
          border-top: 3px solid #007bff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-container {
          text-align: center;
          padding: 40px 20px;
          color: #6c757d;
        }

        @media (max-width: 768px) {
          .rating-prompt-content {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }

          .rating-prompt-actions {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

/* ============================
   Requests Tab + Cards
   ============================ */

const RequestsTab = ({ requests, onUpdate, currentUserId, onViewMessage, onDenyRequest }) => {
  const [requestsSubTab, setRequestsSubTab] = useState('sent');

  const sentRequests = requests.filter(req =>
    currentUserId && req?.requester?.user?.id === currentUserId
  );

  const receivedRequests = requests.filter(req =>
    currentUserId && req?.host?.user?.id === currentUserId
  );

  const currentRequests = requestsSubTab === 'sent' ? sentRequests : receivedRequests;

  return (
    <div className="requests-tab">
      <div className="tab-header">
        <h3>My Requests</h3>
        <div className="sub-tabs">
          <button
            className={`sub-tab ${requestsSubTab === 'sent' ? 'active' : ''}`}
            onClick={() => setRequestsSubTab('sent')}
          >
            Sent ({sentRequests.length})
          </button>
          <button
            className={`sub-tab ${requestsSubTab === 'received' ? 'active' : ''}`}
            onClick={() => setRequestsSubTab('received')}
          >
            Received ({receivedRequests.length})
          </button>
        </div>
      </div>

      {currentRequests.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📝</div>
          <h4>No {requestsSubTab} requests yet</h4>
          <p>
            {requestsSubTab === 'sent'
              ? "Start by browsing clubs and sending requests to play rounds!"
              : "When members request to play at your club, they'll appear here."
            }
          </p>
        </div>
      ) : (
        <div className="requests-list">
          {currentRequests.map(request => (
            <RequestCard
              key={request.request_id}
              request={request}
              isReceived={requestsSubTab === 'received'}
              currentUserId={currentUserId}
              onViewMessage={() => onViewMessage(request)}
              onDenyRequest={onDenyRequest}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const RequestCard = ({ request, isReceived, currentUserId, onViewMessage, onDenyRequest }) => {
  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      scheduled: 'blue',
      confirmed: 'green',
      declined: 'red',
      completed: 'purple',
      cancelled: 'gray',
      expired: 'gray'
    };
    return colors[status] || 'gray';
  };

  const canViewMessage = () => {
    if (isReceived && request.status === 'pending') return true;
    if (['scheduled', 'confirmed', 'completed'].includes(request.status)) return true;
    if (!isReceived && !['declined', 'cancelled', 'expired'].includes(request.status)) return true;
    return false;
  };

  const getButtonText = () => {
    if (isReceived && request.status === 'pending') return 'View Message';
    return 'View Chat';
  };

  return (
    <div className="request-card">
      <div className="request-info">
        <h4>{request?.target_club?.name || 'Club'}</h4>
        <p>
          Status: <span className={`status ${getStatusColor(request.status)}`}>
            {request.status}
          </span>
        </p>
        <p>
          {isReceived ? 'Received' : 'Sent'}:{' '}
          {request.created_at ? new Date(request.created_at).toLocaleDateString() : '—'}
        </p>

        {request.scheduled_date && (
          <p>
            Scheduled: {new Date(request.scheduled_date).toLocaleDateString()}
            {request.scheduled_time && ` at ${request.scheduled_time}`}
          </p>
        )}

        {isReceived && request.requester && (
          <div className="requester-details">
            <p>
              From: {request.requester.user?.first_name || request.requester.user?.username || 'Member'}
            </p>
            <p>Home Club: {request.requester.home_club?.name || '—'}</p>
            <p>Handicap: {request.requester.handicap ?? '—'}</p>
          </div>
        )}

        {!isReceived && request.host && (
          <div className="host-details">
            <p>
              Host: {request.host.user?.first_name || request.host.user?.username || 'Member'}
            </p>
            <p>Home Club: {request.host.home_club?.name || '—'}</p>
            <p>Handicap: {request.host.handicap ?? '—'}</p>
          </div>
        )}

        {request.message && (
          <p className="request-message">"{request.message}"</p>
        )}
      </div>

      <div className="request-actions">
        {canViewMessage() && (
          <button className="view-message-button" onClick={onViewMessage}>
            {getButtonText()}
          </button>
        )}
        {isReceived && request.status === 'pending' && (
          <button
            className="deny-button"
            onClick={() => onDenyRequest(request.request_id)}
          >
            Deny
          </button>
        )}
      </div>
    </div>
  );
};

/* ============================
   Experiences Tab + Card
   ============================ */

const ExperiencesTab = ({ experiences, needsRating, onUpdate, currentUserId }) => {
  const [experiencesSubTab, setExperiencesSubTab] = useState('scheduled');

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const scheduledExperiences = experiences.filter(exp => {
    const roundDate = new Date(exp.play_request?.scheduled_date);
    return roundDate >= today;
  });

  const pastExperiences = experiences.filter(exp => {
    const roundDate = new Date(exp.play_request?.scheduled_date);
    return roundDate < today;
  });

  const currentExperiences = experiencesSubTab === 'scheduled' ? scheduledExperiences : pastExperiences;

  // NEW: Show unrated experiences at the top of past experiences
  const sortedCurrentExperiences = experiencesSubTab === 'past'
    ? [...currentExperiences].sort((a, b) => {
        const aIsHost = currentUserId && a.play_request?.host?.user?.id === currentUserId;
        const bIsHost = currentUserId && b.play_request?.host?.user?.id === currentUserId;

        const aHasRating = aIsHost ? a.host_rating : a.visitor_rating;
        const bHasRating = bIsHost ? b.host_rating : b.visitor_rating;

        // Unrated experiences first
        if (!aHasRating && bHasRating) return -1;
        if (aHasRating && !bHasRating) return 1;

        // Then by date (most recent first)
        return new Date(b.play_request?.scheduled_date) - new Date(a.play_request?.scheduled_date);
      })
    : currentExperiences;

  return (
    <div className="experiences-tab">
      <div className="tab-header">
        <h3>My Experiences</h3>
        <div className="sub-tabs">
          <button
            className={`sub-tab ${experiencesSubTab === 'scheduled' ? 'active' : ''}`}
            onClick={() => setExperiencesSubTab('scheduled')}
          >
            Scheduled ({scheduledExperiences.length})
          </button>
          <button
            className={`sub-tab ${experiencesSubTab === 'past' ? 'active' : ''}`}
            onClick={() => setExperiencesSubTab('past')}
          >
            Past ({pastExperiences.length})
            {needsRating.length > 0 && (
              <span className="needs-rating-indicator">
                {needsRating.length} need rating
              </span>
            )}
          </button>
        </div>
      </div>

      {sortedCurrentExperiences.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">⭐</div>
          <h4>No {experiencesSubTab} experiences</h4>
          <p>
            {experiencesSubTab === 'scheduled'
              ? "Once you confirm rounds, they'll appear here."
              : "After completing rounds, you can rate your experiences here."
            }
          </p>
        </div>
      ) : (
        <div className="experiences-list">
          {sortedCurrentExperiences.map(experience => (
            <ExperienceCard
              key={experience.experience_id}
              experience={experience}
              isPast={experiencesSubTab === 'past'}
              currentUserId={currentUserId}
              needsRating={needsRating.some(nr => nr.experience_id === experience.experience_id)}
              onUpdate={onUpdate}
            />
          ))}
        </div>
      )}

      {/* NEW: CSS for needs rating indicator */}
      <style jsx>{`
        .needs-rating-indicator {
          background: #dc3545;
          color: white;
          font-size: 11px;
          padding: 2px 6px;
          border-radius: 8px;
          margin-left: 8px;
          font-weight: 500;
        }
      `}</style>
    </div>
  );
};

const ExperienceCard = ({ experience, isPast, currentUserId, needsRating, onUpdate }) => {
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isHost = currentUserId && experience.play_request?.host?.user?.id === currentUserId;
  const hasUserRated = isHost ? experience.host_rating : experience.visitor_rating;

  // Lock body scroll when modal open
  useEffect(() => {
    if (showRating) {
      const { scrollY } = window;
      document.body.style.top = `-${scrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      return () => {
        const y = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(y || '0') * -1);
      };
    }
  }, [showRating]);

  const submitRating = async () => {
    setIsSubmitting(true);
    try {
      const payload = isHost
        ? { host_rating: rating, host_feedback: feedback }
        : { visitor_rating: rating, visitor_feedback: feedback };

      await axiosInstance.post(`/member-portal/play-experiences/${experience.experience_id}/rate/`,
        payload,
      );

      setShowRating(false);
      setRating(5);
      setFeedback('');
      onUpdate();

      // Show success message
      alert('Thank you for rating your experience!');
    } catch (error) {
      console.error('Error submitting rating:', error);
      alert('Error submitting rating. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const otherPlayerName = isHost
    ? (experience.play_request?.requester?.user?.first_name ||
       experience.play_request?.requester?.user?.username || 'Member')
    : (experience.play_request?.host?.user?.first_name ||
       experience.play_request?.host?.user?.username || 'Member');

  return (
    <div className={`experience-card ${needsRating ? 'needs-rating' : ''}`}>
      {needsRating && (
        <div className="needs-rating-banner">
          <span>⭐ Ready to Rate</span>
        </div>
      )}

      <div className="experience-info">
        <h4>{experience.play_request?.target_club?.name || 'Club'}</h4>
        <p>
          Date: {experience.play_request?.scheduled_date
            ? new Date(experience.play_request.scheduled_date).toLocaleDateString()
            : '—'}
        </p>
        {experience.play_request?.scheduled_time && (
          <p>Time: {experience.play_request.scheduled_time}</p>
        )}
        <p>
          {isHost ? 'Guest' : 'Host'}: {otherPlayerName}
        </p>

        {isPast && (
          <div className="ratings-display">
            {experience.host_rating && (
              <p>Host Rating: {'⭐'.repeat(experience.host_rating)} ({experience.host_rating}/5)</p>
            )}
            {experience.visitor_rating && (
              <p>Visitor Rating: {'⭐'.repeat(experience.visitor_rating)} ({experience.visitor_rating}/5)</p>
            )}
            {experience.host_feedback && (
              <p className="feedback">Host: "{experience.host_feedback}"</p>
            )}
            {experience.visitor_feedback && (
              <p className="feedback">Visitor: "{experience.visitor_feedback}"</p>
            )}
          </div>
        )}
      </div>

      <div className="experience-actions">
        {isPast && !hasUserRated && (
          <button
            className="rate-button primary"
            onClick={() => setShowRating(true)}
          >
            Rate Experience
          </button>
        )}
      </div>

      {showRating && (
        <div className="piq-rating-modal" role="dialog" aria-modal="true">
          <div className="piq-modal-backdrop" onClick={() => setShowRating(false)} />
          <div className="piq-modal-content" role="document">
            <button
              className="piq-modal-close"
              aria-label="Close"
              onClick={() => setShowRating(false)}
            >
              ×
            </button>
            <h3>Rate Your Experience with {otherPlayerName}</h3>
            <p className="modal-subtitle">
              {experience.play_request?.target_club?.name} •{' '}
              {experience.play_request?.scheduled_date
                ? new Date(experience.play_request.scheduled_date).toLocaleDateString()
                : ''}
            </p>

            <div className="rating-input">
  <label>Rate your round (1–5)</label>

  <div className="piq-scale" role="radiogroup" aria-label="Round rating 1 to 5">
    {[1,2,3,4,5].map((n) => (
      <button
        key={n}
        type="button"
        className={`piq-scale-bar ${rating >= n ? 'active' : ''}`}
        onClick={() => setRating(n)}
        role="radio"
        aria-checked={rating === n}
        aria-label={`${n} of 5`}
      />
    ))}
  </div>

  <div className="piq-scale-ticks" aria-hidden="true">
    <span>1</span><span>3</span><span>5</span>
  </div>

  <p className="rating-description">
    {rating === 1 && 'Poor'}
    {rating === 2 && 'Below Average'}
    {rating === 3 && 'Average'}
    {rating === 4 && 'Good'}
    {rating === 5 && 'Excellent'}
  </p>
</div>

            <div className="feedback-input">
              <label>Feedback (optional)</label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                placeholder={`Share your experience with ${otherPlayerName}…`}
                maxLength={500}
              />
              <small>{feedback.length}/500 characters</small>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowRating(false)}
                disabled={isSubmitting}
                className="secondary-button"
              >
                Cancel
              </button>
              <button
                onClick={submitRating}
                disabled={isSubmitting}
                className="primary-button"
              >
                {isSubmitting ? 'Submitting…' : 'Submit Rating'}
              </button>
            </div>
          </div>

          {/* Namespaced, collision-proof modal styles */}
          <style jsx>{`
            .piq-rating-modal {
              position: fixed;
              inset: 0;
              z-index: 1000;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom));
              height: 100dvh;
            }

            .piq-modal-backdrop {
              position: absolute;
              inset: 0;
              background: rgba(0,0,0,0.52);
              animation: piqFadeIn 120ms ease-out;
            }

            .piq-modal-content {
              position: relative;
              background: #fff;
              width: 100%;
              max-width: 560px;
              border-radius: 16px;
              box-shadow: 0 24px 60px rgba(0,0,0,0.25);
              border: 1px solid rgba(17,24,39,0.06);
              padding: 24px;
              max-height: calc(100dvh - 64px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
              overflow-y: auto;
              animation: piqSlideUp 180ms cubic-bezier(.22,.7,.25,1);
              will-change: transform, opacity;
            }

            .piq-modal-close {
              position: absolute;
              top: 8px;
              right: 10px;
              width: 36px;
              height: 36px;
              border-radius: 10px;
              border: 1px solid #e5e7eb;
              background: #fff;
              font-size: 22px;
              line-height: 1;
              cursor: pointer;
              transition: transform .12s ease, background .15s ease;
            }
            .piq-modal-close:hover { background: #f9fafb; transform: translateY(-1px); }

            @keyframes piqFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes piqSlideUp {
              from { opacity: 0; transform: translateY(16px); }
              to { opacity: 1; transform: translateY(0); }
            }

            .modal-subtitle {
              color: #6c757d;
              margin-bottom: 20px;
              font-size: 0.95rem;
            }

            .star-rating {
              display: flex;
              gap: 6px;
              margin: 10px 0;
            }

            .star {
              background: none;
              border: 1px solid transparent;
              font-size: 1.8rem;
              cursor: pointer;
              opacity: 0.35;
              border-radius: 8px;
              padding: 4px;
              transition: transform .12s ease, opacity .12s ease, border-color .12s ease;
            }
            .star.active { opacity: 1; transform: scale(1.04); }
            .star:hover { opacity: 0.85; border-color: #e5e7eb; }

            .rating-description {
              color: #004d40;
              font-size: 0.95rem;
              margin-top: 6px;
              font-weight: 600;
            }

            .feedback-input { margin: 14px 0; }
            .feedback-input textarea {
              width: 100%;
              border: 1px solid #e5e7eb;
              border-radius: 10px;
              padding: 12px 14px;
              font-family: inherit;
              min-height: 96px;
              resize: vertical;
            }
            .feedback-input small { color: #6b7280; }

            .modal-actions {
              display: flex;
              gap: 12px;
              justify-content: flex-end;
              margin-top: 18px;
            }

            .primary-button {
              background: #004d40;
              color: #fff;
              border: none;
              padding: 10px 20px;
              border-radius: 10px;
              font-weight: 600;
              cursor: pointer;
              transition: filter .15s ease, transform .06s ease;
            }
            .primary-button:hover:not(:disabled) { filter: brightness(.95); transform: translateY(-1px); }
            .primary-button:disabled { opacity: .6; cursor: not-allowed; }

            .secondary-button {
              background: #fff;
              color: #374151;
              border: 1px solid #e5e7eb;
              padding: 10px 20px;
              border-radius: 10px;
              font-weight: 600;
              cursor: pointer;
              transition: background .15s ease;
            }
            .secondary-button:hover:not(:disabled) { background: #f9fafb; }

            /* Mobile: gently bias towards a higher placement while staying centered */
            @media (max-width: 480px) {
              .piq-modal-content {
                border-radius: 16px;
              }
              .star { font-size: 2rem; }
            }
          `}</style>
        </div>
      )}

      {/* NEW: CSS for rating badge + experience card */}
      <style jsx>{`
        .needs-rating {
          border: 2px solid #004d40;
          position: relative;
        }
        .needs-rating-banner {
          background: #004d40;
          color: white;
          padding: 4px 12px;
          font-size: 12px;
          font-weight: 600;
          position: absolute;
          top: -2px;
          right: -2px;
          border-radius: 0 6px 0 6px;
        }
        .rate-button.primary {
          background: #004d40;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: filter .15s ease, transform .06s ease;
        }
        .rate-button.primary:hover { filter: brightness(.95); transform: translateY(-1px); }
      `}</style>
    </div>
  );
};

/* ============================
   Member Card Tab
   ============================ */
const toTitleCase = (s = '') =>
  s.toLowerCase().replace(/\b\w/g, (m) => m.toUpperCase());

const formatLocation = (city, state) => {
  const c = city ? toTitleCase(city) : '';
  const s = state ? String(state).toUpperCase() : '';
  return [c, s].filter(Boolean).join(', ');
};

// Member Card Tab
const MemberCardTab = ({ memberProfile, hostingEnabled, onToggleHosting }) => {
  const [profile, setProfile] = React.useState(memberProfile);
  const [isEditing, setIsEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [form, setForm] = React.useState({
    handicap: profile?.handicap ?? '',
    playing_level: profile?.playing_level ?? '',
    member_since: profile?.member_since ?? '',
    bio: profile?.bio ?? '',
    preferred_days: profile?.preferred_days ?? '',
    preferred_tee_time: profile?.preferred_tee_time ?? '',
  });

  React.useEffect(() => {
    setProfile(memberProfile);
    setForm({
      handicap: memberProfile?.handicap ?? '',
      playing_level: memberProfile?.playing_level ?? '',
      member_since: memberProfile?.member_since ?? '',
      bio: memberProfile?.bio ?? '',
      preferred_days: memberProfile?.preferred_days ?? '',
      preferred_tee_time: memberProfile?.preferred_tee_time ?? '',
    });
  }, [memberProfile]);

  const onChange = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const saveProfile = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.patch('/member-portal/profiles/my_profile/', form);
      setProfile((p) => ({ ...p, ...(data || form) }));
      setIsEditing(false);
    } catch (err) {
      console.error('Save profile failed:', err);
      alert('Unable to save your changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="member-card-tab">
      <div className="tab-header">
        <h3>Member Card</h3>
        <p>Your verified member information</p>
      </div>

      <div className="member-card">
        <div className="card-header">
          <div className="member-avatar">
            {profile?.user?.first_name?.[0] || profile?.user?.username?.[0] || 'M'}
          </div>
          <div className="member-details">
            <h2>{profile?.user?.first_name} {profile?.user?.last_name || profile?.user?.username}</h2>
            <p className="member-since">
             {profile?.home_club?.name || 'Not specified'} Member since {profile?.member_since ? new Date(profile.member_since).getFullYear() : 'N/A'}
            </p>
          </div>
        </div>

        <div className="card-body">
          {!isEditing ? (
            <>
              <div className="info-grid">
                {/* Home Club (read-only) */}
                <div className="info-item">
                  <span className="label">Home Club</span>
                  <span className="value"><strong>{profile?.home_club?.name || 'Not specified'}</strong></span>
                  <span className="value home-club-sub">
                    {(() => {
                      const city = profile?.home_club?.city || '';
                      const state = profile?.home_club?.state || '';
                      const toTitleCase = (s='') => s.toLowerCase().replace(/\b\w/g, m => m.toUpperCase());
                      const loc = [city ? toTitleCase(city) : '', state ? String(state).toUpperCase() : '']
                        .filter(Boolean).join(', ');
                      return loc || '—';
                    })()}
                  </span>
                </div>

                {/* Hosting Status with switch */}
                <div className="info-item hosting-status">
                  <span className="label">Hosting Status</span>
                  <label className={`switch ${hostingEnabled ? 'on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={!!hostingEnabled}
                      onChange={(e) => onToggleHosting?.(e.target.checked)}
                      aria-label="Toggle hosting availability"
                    />
                    <span className="switch-slider" aria-hidden="true"></span>
                  </label>
                  <span className="switch-text">
                    {hostingEnabled ? 'Active Host' : 'Not Hosting - Cannot Browse Clubs'}
                  </span>
                </div>

                <div className="info-item">
                  <span className="label">Handicap</span>
                  <span className="value">{profile?.handicap || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Playing Level</span>
                  <span className="value">{profile?.playing_level || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Preferred Days</span>
                  <span className="value">{profile?.preferred_days || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Preferred Tee Time</span>
                  <span className="value">{profile?.preferred_tee_time || '—'}</span>
                </div>
              </div>

              {profile?.bio && (
                <div className="bio-section">
                  <h4>About</h4>
                  <p>{profile.bio}</p>
                </div>
              )}

              <div className="edit-actions">
                <button className="edit-button" onClick={() => setIsEditing(true)}>
                  Edit Profile
                </button>
              </div>
            </>
          ) : (
            <>
              {/* existing edit form unchanged */}
              <div className="form-grid">
                <div className="form-group">
                  <label>Home Club</label>
                  <input value={profile?.home_club?.name || 'Not specified'} disabled />
                  <p className="readonly-note">
                    To change your home club, email <a href="mailto:support@privateiq.golf">support@privateiq.golf</a>.
                  </p>
                </div>

                <div className="form-group">
                  <label>Handicap</label>
                  <input type="number" step="0.1" value={form.handicap} onChange={onChange('handicap')} />
                </div>

                <div className="form-group">
                  <label>Playing Level</label>
                  <select value={form.playing_level} onChange={onChange('playing_level')}>
                    <option value="">Select level</option>
                    <option value="Casual">Casual</option>
                    <option value="Competitive">Competitive</option>
                    <option value="Tournament Player">Tournament Player</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Preferred Days</label>
                  <input value={form.preferred_days} onChange={onChange('preferred_days')} placeholder="e.g., Weekends, Friday afternoons" />
                </div>

                <div className="form-group">
                  <label>Preferred Tee Time</label>
                  <select value={form.preferred_tee_time} onChange={onChange('preferred_tee_time')}>
                    <option value="">Select preference</option>
                    <option value="Early Morning (7-9 AM)">Early Morning (7-9 AM)</option>
                    <option value="Morning (9-11 AM)">Morning (9-11 AM)</option>
                    <option value="Afternoon (1-3 PM)">Afternoon (1-3 PM)</option>
                    <option value="Late Afternoon (3-5 PM)">Late Afternoon (3-5 PM)</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>

                <div className="form-group form-span-2">
                  <label>About</label>
                  <textarea rows={3} value={form.bio} onChange={onChange('bio')} placeholder="Tell other members about yourself..." />
                </div>
              </div>

              <div className="edit-actions">
                <button className="secondary-button" onClick={() => setIsEditing(false)} disabled={saving}>
                  Cancel
                </button>
                <button className="primary-button" onClick={saveProfile} disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};


/* ============================
   Browse Clubs Tab
   ============================ */

// Gate shown when hosting disabled
const BrowseGate = ({ onGoToMemberCard }) => (
  <div className="gate-card">
    <div className="gate-emoji">🔒</div>
    <h4>Browse Clubs is unavailable</h4>
    <p>Turn on <strong>Hosting</strong> to discover clubs and send round requests.</p>
    <button className="primary-button" onClick={onGoToMemberCard}>
      Go to Member Card
    </button>
  </div>
);

const BrowseClubsTab = ({ clubs, onSendRequest, userHomeClub, activeRequestClubIds = new Set() }) => {
  const availableClubs = (clubs || []).filter(club => {
    const clubId = club.club_id ?? club.id;
    const homeId = userHomeClub?.club_id ?? userHomeClub?.id;
    const hasHosts = (club.host_count ?? 0) > 0;
    return hasHosts && clubId && clubId !== homeId;
  });

  const showEmptyState = availableClubs.length === 0;

  return (
    <div className="browse-clubs">
      <div className="tab-header">
        <h3>Clubs With Active Hosts</h3>
        <p>Only clubs that currently have verified hosts appear here.</p>
      </div>

      {showEmptyState ? (
        <div className="empty-state">
          <div className="empty-icon">🏌️</div>
          <h4>No clubs have hosts yet</h4>
          <p>
            As more members enable hosting at their clubs, they'll show up here.
            You can invite friends to join and verify to grow the network.
          </p>
        </div>
      ) : (
        <div className="clubs-grid">
          {availableClubs.map(club => {
            const id = club.club_id ?? club.id;
            const slug = club.slug; // now provided by API
            const hasActiveRequest = activeRequestClubIds.has(id);
            return (
              <div key={id} className="club-card">
                <div className="club-card-header">
                  <h4 className="club-card-title">
                    {club.name || club.golf_course || 'Unnamed Club'}
                  </h4>

                  {/* View details icon → slug-first */}
                  {slug && (
                    <Link
                      to={`/club/${slug}`}
                      className="club-details-icon"
                      aria-label={`View details for ${club.name || club.golf_course || 'this club'}`}
                      title="View Club"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth="1.5"
                        stroke="currentColor"
                        className="icon-24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                        />
                      </svg>
                    </Link>
                  )}
                </div>

                {(club.city || club.state) && (
                  <p>{[club.city, club.state].filter(Boolean).join(', ')}</p>
                )}

                <div className="club-stats">
                  <span className="host-count">
                    {(club.host_count ?? 0)} host(s) available
                  </span>
                </div>

                <div className="club-actions">
                  <button
                    className={`request-button ${hasActiveRequest ? 'request-pending' : ''}`}
                    onClick={() => !hasActiveRequest && onSendRequest(club)}
                    disabled={hasActiveRequest}
                    title={hasActiveRequest ? 'You already have an active request at this club' : 'Request a round at this club'}
                  >
                    {hasActiveRequest ? 'Request Pending' : 'Request Round'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MemberPortalDashboard;
